import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Copy, Search, Megaphone, PenSquare, Wallet, Map, Users, Package, Phone, HeartHandshake, Trophy, Zap } from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import { AGENT_VISUALS } from "@/lib/agents";

// Each prompt = ready-to-fire conversation starter for a specific agent
const SECTIONS = [
  {
    id: "growth", title: "Croissance & acquisition", icon: Megaphone, color: "#EC4899",
    prompts: [
      { agent: "clara", title: "Calendrier éditorial LinkedIn 30 jours", text: "Rédige-moi un calendrier éditorial LinkedIn sur 30 jours pour GABRYX, 3 posts par semaine. Thèmes : CSPS, gain de temps, témoignages pilotes, coulisses de startup BTP. Donne titre + accroche + corps + CTA pour chaque post." },
      { agent: "clara", title: "Audit ma présence LinkedIn", text: "Audit ma présence LinkedIn actuelle. Propose 5 axes d'amélioration concrets et mesurables pour la rendre plus efficace pour GABRYX." },
      { agent: "clara", title: "Landing page \"Devenir pilote\"", text: "Crée une landing page de capture email \"Devenir pilote CHANTIX\" : titre, sous-titre, 3 bénéfices, formulaire, témoignages crédibles, FAQ." },
      { agent: "visio", title: "Snapshot exécutif hebdomadaire", text: "Snapshot exécutif de ma traction GABRYX cette semaine : nouveaux inscrits, activation, churn, sources d'acquisition. Format dashboard ASCII." },
      { agent: "gaby", title: "Plan 10 pilotes en 30 jours", text: "On vise 10 pilotes signés sous 30 jours. Reverse-engineer un plan d'attaque détaillé en chiffrant chaque étape (volume d'invites LinkedIn, taux de conversion attendu, RDV nécessaires)." },
    ],
  },
  {
    id: "content", title: "Contenu expert", icon: PenSquare, color: "#F97316",
    prompts: [
      { agent: "casper", title: "Plan d'un livre blanc PPSPS", text: "Rédige le plan détaillé d'un livre blanc gratuit (12 pages) : \"Le PPSPS en 2026 : guide pratique du coordonnateur SPS débutant\". Structure, contenu de chaque chapitre, exemples, check-lists." },
      { agent: "casper", title: "10 posts LinkedIn pédagogiques CSPS", text: "Donne-moi 10 idées de posts LinkedIn pédagogiques sur le CSPS (R4532-2 et suivants) — format : 1 hook + 1 mini-cours + 1 leçon à retenir + CTA." },
      { agent: "clara", title: "Newsletter mensuelle GABRYX", text: "Rédige la newsletter mensuelle GABRYX (HTML) : édito, 3 actualités CSPS, 1 témoignage pilote, 1 conseil pratique, CTA \"rejoins la phase pilote\"." },
      { agent: "clara", title: "10 hooks LinkedIn puissants", text: "Rédige 10 hooks LinkedIn ultra-puissants pour la phase pilote GABRYX, inspirés des meilleurs créateurs SaaS B2B. Hook + 1 phrase d'intro." },
    ],
  },
  {
    id: "finance", title: "Finances & admin (auto-entreprise)", icon: Wallet, color: "#10B981",
    prompts: [
      { agent: "fisco", title: "Prévisionnel financier 2026", text: "GABRYX est une auto-entreprise (BNC services). Mon CA prévisionnel pour 2026 : 50 pilotes gratuits puis 30€/mois × 30 clients en fin d'année. Calcule URSSAF, IR libératoire vs barème, trésorerie nette mois par mois. Donne 3 scénarios pessimiste/médian/optimiste." },
      { agent: "fisco", title: "Quand basculer en SASU ?", text: "À partir de quel CA mensuel ai-je intérêt à quitter l'auto-entreprise pour passer en SASU ? Compare précisément la fiscalité aux paliers 30k€, 50k€, 75k€ et 100k€ de CA annuel." },
      { agent: "fisco", title: "Modèle facture SaaS auto-entrepreneur", text: "Rédige un modèle de facture de souscription SaaS GABRYX (30€/mois HT, mensuel) conforme micro-entreprise franchise en base TVA. Mentions légales complètes." },
      { agent: "fisco", title: "Tarification SaaS optimale", text: "À combien dois-je vendre CHANTIX au lancement payant pour atteindre l'équilibre financier en 12 mois ? Hypothèses : auto-entreprise, 0 salarié, 100€/mois d'outils, 50 clients possibles fin 2026. Calcul détaillé." },
      { agent: "fisco", title: "CGV SaaS B2B", text: "Rédige les CGV (Conditions Générales de Vente) d'un SaaS BTP GABRYX à 30€ HT/mois, engagement mensuel, RGPD compliant, droit français, statut auto-entrepreneur." },
      { agent: "fisco", title: "Mentions légales gabryxai.fr", text: "Rédige les mentions légales et la politique de confidentialité pour le site gabryxai.fr (RGPD, hébergement Emergent, cookies, statut auto-entrepreneur Cédric)." },
    ],
  },
  {
    id: "roadmap", title: "Roadmap produit", icon: Map, color: "#06B6D4",
    prompts: [
      { agent: "opera", title: "Planning sprints 3 mois", text: "Je veux livrer 5 nouvelles features CHANTIX d'ici 3 mois : 1) Export ZIP chantier, 2) Partage public sécurisé, 3) Webhooks API, 4) Multi-tenant agents, 5) App mobile iOS. Rédige le planning Gantt sprint par sprint (2 sem chacun)." },
      { agent: "gaby", title: "Priorisation RICE 10 features", text: "J'ai 50 idées de features remontées par mes pilotes. Aide-moi à prioriser avec la méthode RICE (Reach × Impact × Confidence / Effort). Donne-moi les top 10 à attaquer en premier — pose-moi des questions si tu as besoin de plus d'infos." },
    ],
  },
  {
    id: "hr", title: "Recrutement futur", icon: Users, color: "#8B5CF6",
    prompts: [
      { agent: "rhea", title: "Plan de recrutement 12 mois", text: "Plan de recrutement GABRYX sur 12 mois : à 30 clients je recrute 1 commercial, à 100 clients 1 dev senior, à 200 clients 1 customer success. Donne-moi les fiches de poste détaillées + grilles de salaire (BTP/SaaS) pour chaque poste." },
      { agent: "rhea", title: "Offre freelance Customer Success", text: "Rédige une offre d'emploi alléchante pour un \"Customer Success Manager BTP\" en télétravail à temps partiel — 2 jours/semaine, payé 600€/jour HT, freelance." },
    ],
  },
  {
    id: "phone", title: "Standard téléphonique", icon: Phone, color: "#A855F7",
    prompts: [
      { agent: "voxy", title: "Script d'accueil pro GABRYX", text: "Script complet d'accueil téléphonique GABRYX : 1) Accroche d'ouverture, 2) Trame de qualification (entreprise, taille, douleur), 3) Pitch elevator 30 secondes CHANTIX, 4) Routage : si pilote → RDV démo direct ; si curieux → newsletter ; si SAV → ticket." },
      { agent: "voxy", title: "3 annonces vocales", text: "Rédige 3 annonces vocales pour ma messagerie pro : 1) heures de bureau, 2) absence vacances, 3) urgence/sinistre client." },
    ],
  },
  {
    id: "success", title: "Success pilotes", icon: HeartHandshake, color: "#F59E0B",
    prompts: [
      { agent: "chanti", title: "Analyse mes pilotes actifs", text: "Liste mes pilotes actifs sur CHANTIX. Pour chacun identifie : nb de chantiers créés, dernière activité, signal de satisfaction. Propose 1 action de relation pour chaque (call ? email ? demande d'avis ?)." },
      { agent: "clara", title: "Plan Customer Success hebdo", text: "Plan de \"Customer Success\" hebdomadaire : appel de 15 min à chaque pilote toutes les 2 semaines. Trame de questions pour récolter feedback produit + détecter risque de churn." },
    ],
  },
  {
    id: "pitch", title: "Pitch investisseurs (futur)", icon: Trophy, color: "#3B82F6",
    prompts: [
      { agent: "clara", title: "Trame pitch deck 12 slides", text: "Rédige la trame d'un pitch deck GABRYX en 12 slides : problème, solution, marché, traction, business model, équipe, roadmap, ask. Ton confident et chiffré pour une pré-seed 300k€." },
      { agent: "fisco", title: "Modèle financier 3 ans", text: "Modèle financier 3 ans GABRYX : revenus, charges, MRR, ARR, CAC, LTV, break-even. Donne-moi 3 scénarios (pessimiste, médian, optimiste). Hypothèse de départ : bascule en SASU dès 2026 pour permettre la levée." },
      { agent: "gaby", title: "Liste 20 fonds VC français BTP", text: "Liste 20 fonds VC français early-stage qui investissent dans le SaaS BTP. Pour chacun : thèse, ticket moyen, dernier deal pertinent, mail de contact." },
    ],
  },
  {
    id: "ritual", title: "Rituels quotidiens", icon: Zap, color: "#EF4444",
    prompts: [
      { agent: "clara", title: "Action prio de l'après-midi", text: "Sur la base des stats du matin (briefing 07h00), quelle action prioritaire dois-je faire cet après-midi pour faire avancer GABRYX ?" },
      { agent: "gaby", title: "Debrief du soir", text: "Debrief de ma journée : qu'est-ce que j'ai fait, qu'est-ce que j'ai appris, et qu'est-ce que je dois préparer pour demain ?" },
      { agent: "gaby", title: "Routine hebdo 4h", text: "Si j'avais 4 heures par semaine pour faire grandir GABRYX, comment je devrais les utiliser ? Donne-moi une routine hebdo précise heure par heure." },
    ],
  },
];

export default function Playbook() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const launchPrompt = (agent, text) => {
    navigate(`/app/chat/${agent}`, { state: { prefill: text } });
  };

  const copyPrompt = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Prompt copié");
  };

  const lower = q.toLowerCase();
  const sections = SECTIONS.map((s) => ({
    ...s,
    prompts: s.prompts.filter((p) =>
      !q.trim() ||
      p.title.toLowerCase().includes(lower) ||
      p.text.toLowerCase().includes(lower) ||
      p.agent.toLowerCase().includes(lower)
    ),
  })).filter((s) => s.prompts.length > 0);

  return (
    <ConsoleShell
      title="Playbook GABRYX"
      breadcrumb={["GABRYX", "Playbook"]}
      actions={
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un prompt…"
            data-testid="playbook-search"
            className="bg-[#0f0f0f] border border-white/15 pl-8 pr-3 py-1.5 text-xs w-64 focus:border-white/40 outline-none font-mono"
          />
        </div>
      }
    >
      <div className="px-5 lg:px-8 py-6 space-y-6 max-w-[1600px]">
        <div className="text-sm text-zinc-400 max-w-3xl">
          <strong className="text-white">{SECTIONS.reduce((n, s) => n + s.prompts.length, 0)} prompts</strong> prêts à l'emploi pour gérer GABRYX (auto-entreprise) avec ton équipe d'agents IA. Clique sur "Lancer" pour ouvrir la conversation avec le prompt pré-rempli.
        </div>

        {sections.length === 0 ? (
          <div className="border border-white/10 bg-[#0f0f0f] p-12 text-center text-sm text-zinc-500">
            Aucun prompt ne correspond à "{q}"
          </div>
        ) : sections.map((s) => {
          const SIcon = s.icon;
          return (
            <section key={s.id} data-testid={`playbook-section-${s.id}`} className="border border-white/10 bg-[#0f0f0f]">
              <header className="flex items-center gap-3 px-5 py-3 border-b border-white/10">
                <div className="w-7 h-7 grid place-items-center flex-shrink-0" style={{ background: s.color + "15", color: s.color }}>
                  <SIcon className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Catégorie</div>
                  <h2 className="font-display text-base font-semibold">{s.title}</h2>
                </div>
                <div className="ml-auto text-[10px] font-mono text-zinc-600">{s.prompts.length} prompt{s.prompts.length > 1 ? "s" : ""}</div>
              </header>

              <ul className="divide-y divide-white/5">
                {s.prompts.map((p, idx) => {
                  const v = AGENT_VISUALS[p.agent];
                  return (
                    <li key={idx} className="px-5 py-3 hover:bg-white/5 transition" data-testid={`prompt-${s.id}-${idx}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-0.5" style={{ boxShadow: `0 0 0 1.5px ${v?.color || "#fff"}` }}>
                          {v?.avatar && <img src={v.avatar} alt={p.agent} className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-display text-sm font-semibold">{p.title}</span>
                            <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-500">→ {p.agent}</span>
                          </div>
                          <div className="text-[12px] text-zinc-400 leading-relaxed line-clamp-2">{p.text}</div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => copyPrompt(p.text)}
                            className="p-1.5 text-zinc-500 hover:text-white hover:bg-white/5 transition"
                            title="Copier le prompt"
                            data-testid={`prompt-copy-${s.id}-${idx}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => launchPrompt(p.agent, p.text)}
                            data-testid={`prompt-launch-${s.id}-${idx}`}
                            className="text-[11px] bg-white text-black px-2.5 py-1 hover:bg-zinc-200 transition inline-flex items-center gap-1 font-medium"
                          >
                            Lancer <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}

        <div className="text-[10px] text-zinc-600 font-mono pt-2">
          Astuce — ouvre 1 prompt à la fois, attends la réponse, applique. C'est plus efficace que d'ouvrir 5 chats en parallèle.
        </div>
      </div>
    </ConsoleShell>
  );
}
